import React, { useState, useEffect } from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '../../../../messages'
import { useRouter, genomicsRoute } from '@src/routes'
import { ConfigContext } from '../../Genomics'
import {
  FileFormat,
  GenomicProduct,
  ActiveLinkType,
  UploadGenomicsType,
} from '../../enum'
import CircleLoader from '@src/components/CircleLoader'
import { useGenomicsFileUploadMutation } from '@src/mutations/ConfiguratorTubesMutation.generated'
import CautionPopUp from '@src/routes/Configurators/components/CautionPopup'
import {
  ConfiguratorValues,
  templateUrl,
} from '@src/routes/Configurators/utils/constants'
import {
  checkMultipleLine,
  checkMultipleLineWithcolumn,
} from '@src/routes/Configurators/utils/util'
import { Box, Typography } from '@material-ui/core'
import DownloadTemplateLink from '@src/routes/Configurators/components/DownloadTemplateLink'
import UploadFile from '@src/routes/Configurators/components/UploadFile'
import UploadQueriesStepItem from './UploadQueriesStepItem'
import ValidationErrors from '@src/routes/Configurators/validations/ValidationErrors'
import { EventActions } from '@utils/analytics/types'
import { sendConfiguratorGADownloadEvent } from '@utils/analytics/configuratorAnalytics'
import GlobalAlert from '@src/components/GlobalAlert'
import clsx from 'clsx'
import {
  ProductUploadType,
  CrisprInput,
  UploadQueriesProps,
} from '@src/routes/Configurators/components/ConfiguratorTypes'

const useStyles = makeStyles((theme: Theme) => {
  return {
    downloadTemplateLinks: {
      display: 'flex',
      flexDirection: 'column',
    },
    uploadQueriesContainer: {
      marginTop: theme.spacing(6),
      '&$hasPageError': {
        marginTop: theme.spacing(6),
      },
    },
    steps: {
      marginTop: theme.spacing(6),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(6),
    },
    errorAlert: {
      width: 'fit-content',
      marginBottom: theme.spacing(4),
    },
    hasPageError: {},
    links: {
      width: '35%',
      display: 'inline-flex',
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    step3_title: {
      marginTop: theme.spacing(2),
    },
  }
})

const startRow: any = 10
export const parseSpreadsheet = async (
  file?: File,
  productType?: string
): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!file) {
      return resolve('')
    }
    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const XLSX = await import('xlsx')
        const data = new Uint8Array((e.target as any).result)
        const workbook = XLSX.read(data, { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[sheetName]
        const hasHeaders = true
        //const uploadKeys = Object.keys(worksheet)
        //const uploadRangeEnd = uploadKeys[uploadKeys.length - 2]
        let products: CrisprInput[] = []
        let mergedProducts = ''
        if (productType === GenomicProduct.CustomCrispr) {
          if (ProductUploadType.GRNA === sheetName) {
            worksheet['!ref'] = 'A1:B215'
            products = (
              XLSX.utils.sheet_to_json(worksheet, {
                header: ['name', 'sequence'],
              }) as CrisprInput[]
            ).map((oligo) => {
              let { name, sequence } = oligo

              name = name ? name.toString() : ''
              sequence = sequence ? sequence.toString() : ''

              return {
                name,
                sequence,
              }
            })
            if (hasHeaders) {
              products.splice(0, 8)
              checkMultipleLine(products, resolve)
            }
          } else if (ProductUploadType.Nickase === sheetName) {
            worksheet['!ref'] = 'A1:S215'
            products = (
              XLSX.utils.sheet_to_json(worksheet, {
                header: [
                  'laTgtID',
                  'laSequence',
                  'laPam',
                  'laSense',
                  'laStartCoordinate',
                  'raTgtID',
                  'raSequence',
                  'raPam',
                  'raSense',
                  'raStartCoordinate',
                  'distance',
                  'chromosome',
                  'transcripts',
                  'refSeqID',
                  'geneSymbol',
                  'geneID',
                  'species',
                  'laDesignID',
                  'raDesignID',
                ],
              }) as CrisprInput[]
            ).map((oligo) => {
              let {
                laTgtID,
                laSequence,
                laPam,
                laSense,
                laStartCoordinate,
                raTgtID,
                raSequence,
                raPam,
                raSense,
                raStartCoordinate,
                distance,
                chromosome,
                transcripts,
                refSeqID,
                geneSymbol,
                geneID,
                species,
                laDesignID,
                raDesignID,
              } = oligo

              laTgtID = laTgtID ? laTgtID.toString() : ''
              laSequence = laSequence ? laSequence.toString() : ''
              laPam = laPam ? laPam.toString() : ''
              laSense = laSense ? laSense.toString() : ''
              laStartCoordinate = laStartCoordinate
                ? laStartCoordinate.toString()
                : ''
              raTgtID = raTgtID ? raTgtID.toString() : ''
              raSequence = raSequence ? raSequence.toString() : ''
              raPam = raPam ? raPam.toString() : ''
              raSense = raSense ? raSense.toString() : ''
              raStartCoordinate = raStartCoordinate
                ? raStartCoordinate.toString()
                : ''
              distance = distance ? distance.toString() : ''
              chromosome = chromosome ? chromosome.toString() : ''
              transcripts = transcripts ? transcripts.toString() : ''
              //removing  '\n' (leading/trailing) and '\r' characters from reference sequence ID
              //and replace '\n' in middle with '||' symbol
              refSeqID = refSeqID
                ? refSeqID
                    .toString()
                    .replace(/^\n|\n$|\r/g, '')
                    .replace(/\n/g, '||')
                : ''
              geneSymbol = geneSymbol ? geneSymbol.toString() : ''
              geneID = geneID ? geneID.toString() : ''
              species = species ? species.toString() : ''
              laDesignID = laDesignID ? laDesignID.toString() : ''
              raDesignID = raDesignID ? raDesignID.toString() : ''

              return {
                laTgtID,
                laSequence,
                laPam,
                laSense,
                laStartCoordinate,
                raTgtID,
                raSequence,
                raPam,
                raSense,
                raStartCoordinate,
                distance,
                chromosome,
                transcripts,
                refSeqID,
                geneSymbol,
                geneID,
                species,
                laDesignID,
                raDesignID,
              }
            })
            if (hasHeaders) {
              products.splice(0, 7)
              checkMultipleLineWithcolumn(products, resolve, 'laTgtID')
            }
          } else {
            resolve(FileFormat.InvalidTemplate)
          }
        }

        products.forEach((product) => {
          const val = Object.values(product).toString()
          mergedProducts += val + '\n'
        })

        resolve(mergedProducts)
      } catch (err) {
        reject(err)
      }
    }
    reader.readAsArrayBuffer(file)
  })

const UploadQueries = ({ productDetails }: UploadQueriesProps) => {
  const classes = useStyles()
  const { state, dispatch } = React.useContext(ConfigContext)
  const { sessionData, pageError } = state
  const intl = useIntl()
  const router = useRouter()
  const [validationMessage, setValidationMessage] = useState<string[]>([])
  const [genomicsFileUpload, { loading }] = useGenomicsFileUploadMutation()
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [uploadDataType, setUploadDataType] = React.useState('')
  const [datavalue, setDatavalue] = React.useState<any>('')
  const [uploadProductType, setUploadProductType] = React.useState('')
  const { product = '' } = router.query

  const sequencePath = `${genomicsRoute.index()}?product=${product}&activeLink=${
    ActiveLinkType.SequenceEntry
  }`

  useEffect(() => {
    return function cleanupError() {
      dispatch({ type: 'SET_PAGE_ERROR', data: '' })
    }
    // TODO: need a test before we refactor these dependencies
  }, [])

  const setInitialState = () => {
    return {
      name: '',
      geneId: '',
      geneSymbol: '',
      sequence: '',
      id: 1,
      isOverridden: false,
      baseCount: 0,
      isCurrentForm: true,
      listSubModelErrors: [],
      price: '',
      cartId: '',
      itemId: '',
    }
  }

  const updateSessionData = (inputData) => {
    sessionData[0].listSubModels = []
    const lsModelArr: any = []
    inputData[0].listSubModels.forEach((lsModel, index) => {
      const emptyStateObj = Object.assign({}, setInitialState())
      lsModelArr.push(emptyStateObj)
      lsModelArr[index].name = lsModel.name
      lsModelArr[index].geneId = ''
      lsModelArr[index].geneSymbol = ''
      lsModelArr[index].sequence = lsModel.sequence
      lsModelArr[index].id = lsModel.id
      lsModelArr[index].isOverridden = false
      lsModelArr[index].isCurrentForm = lsModel.isCurrentForm
      lsModelArr[index].baseCount = lsModel.baseCount
      lsModelArr[index].listSubModelErrors = lsModel.listSubModelErrors
    })
    sessionData[0].listSubModels = lsModelArr
    dispatch({
      type: 'UPDATE_LIST_SUB_MODEL',
      data: [...sessionData[0].listSubModels],
    })
    const emptyStateObj = Object.assign({}, setInitialState())
    sessionData[0].listSubModels.push(emptyStateObj)
    dispatch({
      type: 'UPDATE_INPUT',
      data: [...sessionData],
    })
    dispatch({
      type: 'UPDATE_CURRENT_INDEX',
      data: lsModelArr.length - 1,
    })
  }
  const performFileUpload = (key, value) => {
    setValidationMessage([])
    if (key && key !== '') {
      if (key === messages.CONF_SUCCESS.defaultMessage && value) {
        dispatch({
          type: 'CRISPR_SEQUENCE_LIST',
          data: true,
        })
        if (state.stepNo !== 0) {
          dispatch({ type: 'SET_STEP_NO', data: 0 })
        }
        const obj: any = JSON.parse(value)
        updateSessionData(obj)
        router.push(sequencePath)
      } else {
        setValidationMessage([key])
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
        })
      }
    }
  }

  const handleUploadChange = async (file) => {
    dispatch({ type: 'SET_PAGE_ERROR', data: '' })
    setValidationMessage([])
    if (
      FileFormat.AcceptFormat &&
      FileFormat.AcceptFormat.split(',')
        .map((type) => type.trim().toLowerCase())
        .includes(file?.type)
    ) {
      const inputStr = await parseSpreadsheet(file, product)
      if (inputStr === FileFormat.InvalidTemplate) {
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
        })
        setValidationMessage([
          intl.formatMessage(messages.CONF_INVALID_TEMPLATE),
        ])
        if (state.stepNo !== 0) {
          dispatch({ type: 'SET_STEP_NO', data: 0 })
        }
      }
      if (inputStr === FileFormat.MultipleLineError) {
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
        })
        setValidationMessage([intl.formatMessage(messages.MULTIPLE_LINE_ERROR)])
        if (state.stepNo !== 0) {
          dispatch({ type: 'SET_STEP_NO', data: 0 })
        }
      }
      if (inputStr === '') {
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
        })
        setValidationMessage([intl.formatMessage(messages.CONF_EMPTY_FILE)])
        if (state.stepNo !== 0) {
          dispatch({ type: 'SET_STEP_NO', data: 0 })
        }
      }
      if (
        inputStr !== '' &&
        inputStr !== FileFormat.MultipleLineError &&
        inputStr !== FileFormat.InvalidTemplate
      ) {
        //make api call here passing inputStr as input
        const response = await genomicsFileUpload({
          fetchPolicy: 'no-cache',
          variables: {
            input: inputStr,
            productInput: productDetails,
            startRow: startRow,
          },
        })
        const { data } = response

        if (data && data.genomicsFileUpload) {
          if (!data.genomicsFileUpload.serverError) {
            const { key, value } = data.genomicsFileUpload
            performFileUpload(key, value)
          } else {
            window.scrollTo(0, 0)
            dispatch({
              type: 'SET_PAGE_ERROR',
              data: ConfiguratorValues.InternalServerError,
            })
          }
        }
      }
    } else {
      if (file !== undefined) {
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
        })
        setValidationMessage([intl.formatMessage(messages.CONF_INVALID_FORMAT)])
        if (state.stepNo !== 0) {
          dispatch({ type: 'SET_STEP_NO', data: 0 })
        }
      }
    }
  }
  const saveToSession = (type, data) => {
    if (type === UploadGenomicsType.fileUpload) {
      handleUploadChange(data)
    }
  }

  const validateUploadFile = (file) => {
    if (
      sessionData.length === 1 &&
      sessionData[0].listSubModels.length === 1 &&
      sessionData[0].listSubModels[0].name === '' &&
      sessionData[0].listSubModels[0].sequence === ''
    ) {
      handleUploadChange(file)
    } else {
      setUploadDataType(UploadGenomicsType.fileUpload)
      setDatavalue(file)
      setDialogOpen(true)
      setUploadProductType(UploadGenomicsType.prodType)
    }
  }

  const handleAnalyticsEvent = (item) =>
    sendConfiguratorGADownloadEvent(
      EventActions.Templates,
      `${item.productType}`
    )

  const downloadLinks = [
    {
      label: 'gRNA Template',
      fileName: 'gRNA_Template.xls',
      productType: 'gRNA',
      url: `${templateUrl.gRNA}?isPCR=false`,
    },
    {
      label: 'Nickase Template',
      fileName: 'Nikase_Template.xls',
      productType: 'Nikase',
      url: `${templateUrl.gRNA}?isPCR=true`,
    },
  ]

  const StepItems = [
    {
      title: <FormattedMessage {...messages.CONF_DNAPLATES_STEP1} />,
      subTitle: <FormattedMessage {...messages.CRISPR_BULK_UPLOAD_STEP2} />,
      stepComponent: (
        <Box className={classes.downloadTemplateLinks}>
          {downloadLinks.map((item, index) => (
            <div key={index} className={classes.links}>
              <DownloadTemplateLink
                item={item}
                key={index}
                onClick={(item) => handleAnalyticsEvent(item)}
              />
            </div>
          ))}
        </Box>
      ),
    },
    {
      title: <FormattedMessage {...messages.CONF_DNAPLATES_STEP2} />,
      subTitle: <FormattedMessage {...messages.CONF_MULTIPLE_ENTRY_STEP_2} />,
      stepComponent: null,
    },
    {
      title: (
        <div className={classes.step3_title}>
          <FormattedMessage {...messages.CONF_DNAPLATES_STEP3} />
        </div>
      ),
      subTitle: (
        <FormattedMessage {...messages.CONF_UPLOAD_YOUR_DATA_customcrispr} />
      ),
      stepComponent: (
        <>
          <UploadFile
            accept={FileFormat.AcceptFormat}
            handleUploadChange={([file]) => validateUploadFile(file)}
            isConfiguratorsRedesign
          />
          <ValidationErrors errors={validationMessage} />
        </>
      ),
    },
  ]

  return (
    <div>
      <Box
        className={clsx(
          classes.uploadQueriesContainer,
          pageError && classes.hasPageError
        )}
      >
        {pageError && (
          <GlobalAlert severity="error" className={classes.errorAlert}>
            {pageError}
          </GlobalAlert>
        )}
        <Typography variant="body1" component="label">
          <FormattedMessage
            {...messages.CONF_OPTIONAL_INTR_MOD_TEMPLATE_standard}
          />
        </Typography>
        <Box className={classes.steps}>
          {StepItems.map((item, index) => (
            <UploadQueriesStepItem
              key={index}
              title={item.title}
              subTitle={item.subTitle}
              stepComponent={item.stepComponent}
            />
          ))}
        </Box>
        {loading && <CircleLoader />}
      </Box>
      <CautionPopUp
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        saveToSession={saveToSession}
        uploadDataType={uploadDataType}
        data={datavalue}
        prodType={uploadProductType}
      />
    </div>
  )
}

export default UploadQueries
